/**
 * Claude starter kit (PR #67, docs/mcp/starter-kit): builds the user's
 * "dealcloser-messaging" skill from what they already filled in DealCloser,
 * so nobody types their offer twice. Same field map as
 * docs/mcp/starter-kit/fill-the-skill.md: the campaign brief wins, the
 * Settings (Offer, Voice, FAQ, Objections) fill the gaps, and anything still
 * empty is listed so the user knows what to add.
 *
 * Kept free of React so it is unit tested. The zip writer exists because
 * claude.ai takes a skill as a zip of a folder holding SKILL.md.
 */
import {
  BRIEF_RULES,
  type CampaignBrief,
  type FaqConfig,
  type ObjectionConfig,
  type OfferConfig,
  type SenderVoiceConfig,
} from '@dealcloser/shared'

export const SKILL_NAME = 'dealcloser-messaging'

export interface SkillSources {
  readonly campaignName: string
  readonly brief: CampaignBrief | null
  readonly offer?: Partial<OfferConfig> | null
  readonly voice?: Partial<SenderVoiceConfig> | null
  readonly faq?: FaqConfig | null
  readonly objections?: ObjectionConfig | null
  /** settings.style_samples / step samples are not exposed here; the brief carries them at run time. */
}

export interface BuiltSkill {
  readonly markdown: string
  /** Section titles that are still empty, in template order. */
  readonly missing: readonly string[]
}

const t = (v: unknown): string => (typeof v === 'string' ? v.trim() : '')
const first = (...vals: unknown[]): string => vals.map(t).find(Boolean) ?? ''
const lines = (vals: readonly unknown[] | undefined): string[] => (vals ?? []).map(t).filter(Boolean)

/** One line, safe inside the YAML front matter. */
function frontMatterText(s: string): string {
  return s.replace(/[\r\n]+/g, ' ').replace(/[<>"]/g, '').trim().slice(0, 80)
}

export function buildMessagingSkill(src: SkillSources): BuiltSkill {
  const b = src.brief
  const offer = src.offer ?? {}
  const voice = src.voice ?? {}
  const missing: string[] = []
  const sections: string[] = []

  const add = (title: string, body: string, opts: { required?: boolean } = {}) => {
    if (body.trim()) sections.push(`## ${title}\n\n${body.trim()}`)
    else if (opts.required !== false) missing.push(title.replace(/ \(.*\)$/, ''))
  }

  add('What we sell', first(b?.what_you_sell, offer.offerDescription, offer.coreOfferOneLiner))
  add('Who it is for', first(b?.who_its_for, offer.targetAudience))
  add('The result customers get', t(b?.outcome))
  add(
    'Proof you may cite (use only this, never invent more)',
    t(b?.proof) || lines([offer.socialProof, offer.caseStudyOneLiner, offer.authorityProof]).join('\n'),
  )
  add('What makes us different', first(b?.differentiator, offer.differentiator))
  add('What you may say about price', t(b?.pricing))

  const ideal = [
    t(b?.ideal_roles) && `- Roles: ${t(b?.ideal_roles)}`,
    t(b?.ideal_companies) && `- Companies: ${t(b?.ideal_companies)}`,
    t(b?.must_haves) && `- Great fit when: ${t(b?.must_haves)}`,
  ].filter(Boolean).join('\n')
  add('Ideal customer', ideal)
  add('Not a fit (if the lead clearly matches this, do not pitch; stay polite and brief)', t(b?.disqualifiers))

  const qa = [
    ...(b?.faqs ?? []).map((f) => [f.question, f.answer] as const),
    ...(src.faq?.entries ?? []).map((f) => [f.question, f.answer] as const),
    ...(src.objections?.entries ?? []).map((o) => [o.objection, o.response] as const),
  ]
    .filter(([q, a]) => t(q) && t(a))
    .map(([q, a]) => `Q: ${t(q)}\nA: ${t(a)}`)
  add('FAQs and objections (answer in your own words, keep the facts)', qa.join('\n\n'))

  add('How a good conversation ends', t(b?.conversation_goal))
  add('Tone', first(b?.tone, voice.profileSummary))
  add(
    'Writing samples (copy the rhythm, not the content)',
    lines([voice.writingSample1, voice.writingSample2]).join('\n\n---\n\n'),
  )
  add('Never say', [t(b?.never_say), ...lines(voice.doNotSay).map((w) => `- ${w}`)].filter(Boolean).join('\n'))
  add('Extra instructions', t(b?.extra_instructions), { required: false })

  const rules = BRIEF_RULES.filter((r) => b?.rules[r.id] ?? r.defaultOn).map((r) => `- ${r.instruction}`)
  add('Rules for this campaign', rules.join('\n'), { required: false })

  const name = frontMatterText(src.campaignName) || 'my campaign'
  const markdown = [
    '---',
    `name: ${SKILL_NAME}`,
    `description: How to write LinkedIn messages for my DealCloser campaign "${name}". Holds the offer, proof, ideal customer, FAQs, objections and tone. Use it whenever you work DealCloser messaging (list_due_messages, get_lead_brief, check_message, submit_message).`,
    '---',
    '',
    `# DealCloser messaging: ${name}`,
    '',
    'Made in DealCloser (Settings, Claude, Starter kit) from my campaign brief and settings. Edit anything that does not sound like me.',
    '',
    '## How to use this skill',
    '',
    '1. Always call `get_lead_brief` before writing. Where its `campaign.brief` is more specific than this skill, follow the brief: it is newer.',
    '2. Follow the `writing_rules` in the lead brief exactly. They are what DealCloser\'s quality check enforces.',
    '3. Only say things written here or in the lead brief. Never invent facts, numbers, results, prices or availability. Never promise to send something later.',
    '4. Run `check_message` before `submit_message`. If it fails, fix the text using the feedback.',
    '5. One next step per message. At most one link, and only a link from the brief.',
    '',
    sections.join('\n\n'),
    '',
    '## What to do with outcomes',
    '',
    'Record every outcome with `update_lead` so nobody messages the lead by mistake:',
    '',
    '- They ask to stop or unsubscribe: `do_not_contact`.',
    '- A clear no: `not_interested`.',
    '- They booked a call somewhere else: `meeting_booked`.',
    '- "Check back next month" or similar: `snooze_until` with that date.',
    '- Anything angry, legal, sensitive, or a pricing question this skill does not answer: `hand_to_human` with a one-line note. Do not guess.',
    '',
  ].join('\n')

  return { markdown, missing }
}

// ---------------------------------------------------------------------------
// Minimal zip writer (stored, no compression). A skill is one small text file,
// so this beats shipping a zip library in the bundle.
// ---------------------------------------------------------------------------

let crcTable: Uint32Array | null = null
export function crc32(data: Uint8Array): number {
  if (!crcTable) {
    crcTable = new Uint32Array(256)
    for (let n = 0; n < 256; n++) {
      let c = n
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
      crcTable[n] = c >>> 0
    }
  }
  let crc = 0xffffffff
  for (let i = 0; i < data.length; i++) crc = crcTable[(crc ^ data[i]!) & 0xff]! ^ (crc >>> 8)
  return (crc ^ 0xffffffff) >>> 0
}

export function makeZip(files: readonly { readonly name: string; readonly data: Uint8Array }[]): Uint8Array<ArrayBuffer> {
  const enc = new TextEncoder()
  const local: Uint8Array[] = []
  const central: Uint8Array[] = []
  let offset = 0
  for (const f of files) {
    const name = enc.encode(f.name)
    const crc = crc32(f.data)
    const head = new DataView(new ArrayBuffer(30))
    head.setUint32(0, 0x04034b50, true)
    head.setUint16(4, 20, true)
    head.setUint16(8, 0, true) // stored
    head.setUint16(12, 0x21, true) // 1980-01-01
    head.setUint32(14, crc, true)
    head.setUint32(18, f.data.length, true)
    head.setUint32(22, f.data.length, true)
    head.setUint16(26, name.length, true)
    local.push(new Uint8Array(head.buffer), name, f.data)

    const dir = new DataView(new ArrayBuffer(46))
    dir.setUint32(0, 0x02014b50, true)
    dir.setUint16(4, 20, true)
    dir.setUint16(6, 20, true)
    dir.setUint16(14, 0x21, true)
    dir.setUint32(16, crc, true)
    dir.setUint32(20, f.data.length, true)
    dir.setUint32(24, f.data.length, true)
    dir.setUint16(28, name.length, true)
    dir.setUint32(42, offset, true)
    central.push(new Uint8Array(dir.buffer), name)
    offset += 30 + name.length + f.data.length
  }
  const centralSize = central.reduce((n, p) => n + p.length, 0)
  const end = new DataView(new ArrayBuffer(22))
  end.setUint32(0, 0x06054b50, true)
  end.setUint16(8, files.length, true)
  end.setUint16(10, files.length, true)
  end.setUint32(12, centralSize, true)
  end.setUint32(16, offset, true)
  const parts = [...local, ...central, new Uint8Array(end.buffer)]
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0))
  let at = 0
  for (const p of parts) {
    out.set(p, at)
    at += p.length
  }
  return out
}

/** The skill as claude.ai wants it: a zip holding dealcloser-messaging/SKILL.md. */
export function skillZip(markdown: string): Uint8Array<ArrayBuffer> {
  return makeZip([{ name: `${SKILL_NAME}/SKILL.md`, data: new TextEncoder().encode(markdown) }])
}

/**
 * "What you can ask Claude" (owner's Loom, 10-24: reply reminders, stats and
 * general reads). Each prompt only needs tools every connection has, except
 * the research one (Pro, a connection that can write). Keep in step with
 * docs/mcp/starter-kit/README.md.
 */
export const ASK_CLAUDE_EXAMPLES: readonly { readonly group: string; readonly prompts: readonly string[] }[] = [
  {
    group: 'Replies and reminders',
    prompts: [
      'Which of my DealCloser leads replied and are waiting on me? Oldest first.',
      'Show my DealCloser review queue: what is waiting for me to approve?',
      'Every weekday at 9 am, tell me which DealCloser leads I still need to answer.',
    ],
  },
  {
    group: 'Stats',
    prompts: [
      'How are my DealCloser campaigns doing this week? Acceptance rate, replies and meetings.',
      'Compare my DealCloser campaigns and tell me which one to put more leads into.',
    ],
  },
  {
    group: 'Leads and conversations',
    prompts: [
      'Find my DealCloser leads with VP in their title and show where each one is.',
      'Show me my whole DealCloser conversation with Jane Doe and suggest a reply.',
      'Research my DealCloser leads that have no research yet.',
    ],
  },
]
